import { DatePicker, Form, Input, message, Select } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useState } from 'react';
import { updateOpportunity } from '@/services/bff/opportunity.api';
import type { OpportunityItem } from '@/services/bff/types/opportunity';
import { BusinessModal } from '../business-modal';

const reasons = {
  on_hold: ['甲方暂缓', '预算冻结', '等待立项或审批', '优先级调整', '其他'],
  closed: [
    '甲方取消项目',
    '预算取消',
    '无限期暂缓，当前无重启计划',
    '选择其他供应商',
    '我方主动放弃',
    '长期失联，经复核关闭',
    '其他',
  ],
  active: [],
};
const titles = {
  active: '恢复跟进',
  on_hold: '搁置商机',
  closed: '关闭商机（未成交）',
};

type Values = { reason?: string; detail?: string; reviewAt?: Dayjs };
export function OpportunityLifecycleModal({
  opportunity,
  status,
  onCancel,
  onSuccess,
}: {
  opportunity: OpportunityItem;
  status: 'active' | 'on_hold' | 'closed';
  onCancel: () => void;
  onSuccess: () => void;
}) {
  const [form] = Form.useForm<Values>();
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [confirmExit, setConfirmExit] = useState(false);
  const cancel = () => {
    if (saving) return;
    if (dirty) setConfirmExit(true);
    else onCancel();
  };
  const submit = async () => {
    let values: Values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    setSaving(true);
    try {
      await updateOpportunity(opportunity.uuid, {
        lifecycleStatus: status,
        ...(status === 'active'
          ? {}
          : {
              statusReason: [values.reason, values.detail?.trim()]
                .filter(Boolean)
                .join('：'),
              ...(status === 'on_hold'
                ? { reviewAt: values.reviewAt!.format('YYYY-MM-DD') }
                : {}),
            }),
      });
      message.success('商机状态已更新');
      onSuccess();
    } catch (error) {
      message.error(
        error instanceof Error ? error.message : '状态更新失败，请重试',
      );
    } finally {
      setSaving(false);
    }
  };
  return (
    <>
      <BusinessModal
        open
        title={titles[status]}
        width={
          status === 'active' ? 'min(520px, calc(100vw - 48px))' : undefined
        }
        onCancel={cancel}
        zIndex={1200}
        closable={{ 'aria-label': '关闭' }}
        keyboard
        maskClosable={false}
        footer={undefined}
        okText={titles[status]}
        onOk={() => void submit()}
        confirmLoading={saving}
        cancelButtonProps={{ disabled: saving }}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={
            status === opportunity.lifecycleStatus
              ? {
                  reason: opportunity.statusReason?.split('：')[0],
                  detail: opportunity.statusReason
                    ?.split('：')
                    .slice(1)
                    .join('：'),
                  reviewAt: opportunity.reviewAt
                    ? dayjs(opportunity.reviewAt)
                    : undefined,
                }
              : {}
          }
          onValuesChange={() => setDirty(true)}
        >
          <p>
            {status === 'active'
              ? '恢复后继续从原销售阶段跟进，历史原因与操作记录将保留。'
              : '保留当前销售阶段，退出活跃商机及当期预计成交统计。'}
          </p>
          {status !== 'active' ? (
            <>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns:
                    'repeat(auto-fit, minmax(min(100%, 260px), 1fr))',
                  columnGap: 16,
                }}
              >
                <Form.Item
                  name="reason"
                  label="原因"
                  rules={[{ required: true, message: '请选择原因' }]}
                >
                  <Select
                    options={reasons[status].map((value) => ({
                      label: value,
                      value,
                    }))}
                  />
                </Form.Item>
                {status === 'on_hold' ? (
                  <Form.Item
                    name="reviewAt"
                    label="下次复查日期"
                    rules={[
                      { required: true, message: '请选择复查日期' },
                      {
                        validator: (_, value) =>
                          !value || !value.isBefore(dayjs(), 'day')
                            ? Promise.resolve()
                            : Promise.reject(new Error('复查日期不能早于今天')),
                      },
                    ]}
                  >
                    <DatePicker format="YYYY-MM-DD" style={{ width: '100%' }} />
                  </Form.Item>
                ) : null}
              </div>
              <Form.Item
                name="detail"
                label="补充说明"
                rules={[{ max: 450, message: '补充说明最多450字' }]}
              >
                <Input.TextArea rows={3} maxLength={450} showCount />
              </Form.Item>
            </>
          ) : null}
        </Form>
      </BusinessModal>
      {confirmExit ? (
        <BusinessModal
          open
          title="放弃未保存的修改？"
          width="min(520px, calc(100vw - 48px))"
          zIndex={1300}
          closable={{ 'aria-label': '关闭' }}
          keyboard
          footer={undefined}
          onCancel={() => setConfirmExit(false)}
          onOk={onCancel}
          okText="放弃修改"
        >
          已填写的原因和复查日期尚未保存。
        </BusinessModal>
      ) : null}
    </>
  );
}
